//////////////////////////////////////////////////////////////
// Watermark Screen

import m from "mithril";
import dayjs from 'dayjs'
import {filesize} from 'filesize'

import {
	defineWidget, Empty, CardieWidget, NoticeWidget, FileSelectButton, ButtonNew, confirm
} from "@nebularstreams/libmui"


import {loadContent} from "../Api";
import MAINSTATE, {STATE_WATERMARK as STATE} from "../State";

import {initUpload, uploadFile} from "./misc/Uploader";
import {TitleBarWidget} from "./misc/TitleBarWidget";

//////////////////////////////////////////////////////////////

const

	// screen strings

	STR = {
		YOUR_WATERMARKS: "Your Watermarks in nanoStream Storage",
		CURRENT_WATERMARK: "current watermark",
		EMPTY_WATERMARKS: "No watermarks in your nanoStream Storage\nPlease upload some by pressing the button below.",
		NO_WATERMARK: "No watermark selected",
		SET_WATERMARK: "Set As Default Watermark",
		SET_WATERMARK_CONFIRM: "Set selected watermark as default watermark?",
		DEFAULT_WATERMARK: "This is the default watermark: It will be used if no specific background is requested when setting up the stream",
		UPLOAD_WATERMARKS: "Upload watermarks to your storage",
		TITLE: "watermark - nanoStream Studio",
		WATERMARK: "Watermark",
		STUDIO: "Studio"
	};

//////////////////////////////////////////////////////////////

const {service: SERVICE} = STATE;

//////////////////////////////////////////////////////////////

let _watermarkListRefreshTimer;

function postEnableWatermark(organization, invalidate, timeout = 2000) {
	clearTimeout(_watermarkListRefreshTimer);
	_watermarkListRefreshTimer = setTimeout(enableWatermark, timeout, organization, invalidate);
}

/**
 * Load the watermarks for the orga
 * @param organization
 * @param invalidate Whether to invalidate list cache
 */

function enableWatermark(organization, invalidate = false) {

	organization = organization.split("-")[0];

	if (!invalidate && STATE.organization === organization && STATE.content?.list?.length) {
		m.redraw()
		return;
	}

	Object.assign(STATE, {
		loadingContent: true,
		organization
	});

	const streamname = organization + "-xxxxx";

	m.redraw();

	loadContent(STATE, streamname, "png")
		.catch((e) => {
			STATE.error = e.message;
		})
		.finally(() => {
			Object.assign(STATE, {
				loadingContent: false,
				streamname
			});
			m.redraw()
		})
}

/**
 * Uploads the current image as the default watermark
 * @returns {Promise<void>}
 */

async function setAsDefaultWatermark() {

	// basically upload current into default.png
	if (!STATE.currentWatermark) return;

	const

		imageUrl = [STATE.content.webroot, STATE.currentWatermark.key].join("/"),
		response = await fetch(imageUrl),
		blob = await response.blob(),

		// create a File object to re-use File Uploading service
		fakeFile = new File([blob], "default.png", {type: blob.type});

	await uploadFile(STATE, fakeFile, {refresh: () => m.redraw()});

	// update UI
	enableWatermark(STATE.organization, true);

	// affects default.png cache
	STATE.cacheKey = Date.now();

	m.redraw();
}

//////////////////////////////////////////////////////////////
// UI WIDGETS

const

	// widget: The Bucket Item
	ContentItem = defineWidget(
		({attrs}) => m("li.flexcol",
			{
				onclick: attrs.onClick,
				class: (attrs.item.key.endsWith("/default.png") ? "default " : "") + (attrs.class || "")
			},
			[
				m("div.embedimage", [
					m("img", {
						alt: `${STR.CURRENT_WATERMARK} ${attrs.item.key.split("/").pop()}`,
						src: `${[STATE.content.webroot, attrs.item.key].join("/")}?_${STATE.cacheKey}`
					})
				]),
				m("span.name.grows.ellipsis.ohidden", attrs.item.key.split(`watermark/${STATE.organization}/`)[1]),
				m("span.size.col-2", filesize(attrs.item.size)),
				m("span.date.col-2", dayjs(attrs.item.date).fromNow()),
			])
	),

	// widget: Bucket List
	WatermarkBrowser = defineWidget(
		() => m(CardieWidget,
			{
				cols: true,
				class: "bucketlist",
				title: `${STR.YOUR_WATERMARKS} ${STATE.content?.size ? `(${filesize(STATE.content.size)})` : ""}`,
				bodyClass: "scrolly",
				element: "div",
				alwaysView: () => m(".flexrow.footer", [
					m(FileSelectButton, {
						text: STR.UPLOAD_WATERMARKS,
						accept: "image/png",
						onFile: (file) => {
							uploadFile(STATE, file, {loop: false})
								.then(() => postEnableWatermark(STATE.organization, true))
								.finally(() => m.redraw());
						}
					})
				])
			},
			[
				...STATE.uploadingFiles.length
					? [
						m("ul.flexcol.w-100.uploading",
							STATE.uploadingFiles.map((item) => m("li.uploadingvideo", {
									id: item.id,
									"data-file": item.file
								}, [
									m(".progress", {
										style: {
											width: `${item.percentage || 0}%`
										}
									})
								])
							)
						)]
					: [],

				...STATE.loadingContent
					? [m(Empty, {icon: "sync.fa-spin", size: 2, class: "pad-2"})]
					: STATE.content?.list?.length
						? [
							m("ul.flexrow.wrap.grows.w-100.gap-2", STATE.content.list.map((item) => m(ContentItem, {
								item,
								webroot: STATE.content.webroot,
								class: "wm " + (STATE.currentWatermark === item ? "selected" : ""),
								onClick: () => {
									STATE.currentWatermark = item;
									m.redraw();
								}
							})))
						]
						: STATE.content?.ts
							? [m(Empty, {
								size: 2,
								icon: "video-slash",
								message: STR.EMPTY_WATERMARKS
							})]
							: []
			]
		),
		undefined,
		({dom}) => initUpload(
			STATE,
			dom,
			{
				type: "png",
				onUpload: () => postEnableWatermark(STATE.organization, true),
				refresh: () => m.redraw()
			}
		)
	),

	// widget: Preview Watermark
	WatermarkPreviewWidget = defineWidget(
		() => m(CardieWidget, {
				class: "playlist part-right",
				title: `${STR.WATERMARK}${STATE.currentWatermark ? ` - ${STATE.currentWatermark.key.split("/").pop().split(".png")[0]}` : ""}`,
				element: "div",
				bodyClass: "scrolly",
				cols: true,
				nofold: true,
			},
			[
				m(".tvscreen.flexcol", STATE.currentWatermark
					? [
						m("div.image", {
							class: "image169 relative ohidden"
						}, [
							m("img", {
								alt: "watermark " + STATE.currentWatermark.key.split("/").pop(),
								src: ([STATE.content.webroot, STATE.currentWatermark.key].join("/")) + "?_" + STATE.cacheKey
							})
						])
					]
					: [
						m(Empty, {
							class: "darkie",
							icon: "image",
							message: STR.NO_WATERMARK
						})
					]),
				...STATE.currentWatermark && !STATE.currentWatermark.key.endsWith("/default.png")
					? [
						m(ButtonNew, {
							class: "unitheight colorbutton orange",
							icon: "stamp",
							text: STR.SET_WATERMARK,
							onClick: () => confirm(STR.SET_WATERMARK_CONFIRM, () => setAsDefaultWatermark())
						})
					]
					: STATE.currentWatermark
						? [
							m(NoticeWidget, {
								class: "notinorm",
								icon: "info-circle",
								text: STR.DEFAULT_WATERMARK
							})
						]
						: []

			]
		)
	),

	// widget: Main Screen
	WatermarkScreen = defineWidget(
		() => [
			m(TitleBarWidget, {
				title: STR.WATERMARK,
				rootTitle: STR.STUDIO,
				rootTitleClick: () => m.route.set(`/${STATE.organization}`)
			}),
			m(".flexcol.h-100.ohidden.grows.scrolly.outercontent",
				m(".flexcol.grows.noshrink.innercontent", [
					m(".flexrow.ohidden.maincontainer",
						STATE.streamname
							? [
								m(".flexcol.grows.ohidden.part-left", [

									...STATE.content?.ts ? [m(WatermarkBrowser)] : [],
									...STATE.error
										? [
											m(NoticeWidget, {
												class: "rednotice nogrows marg-2",
												icon: "exclamation-triangle",
												text: STATE.error,
												onClick: () => {
													delete STATE.error;
													m.redraw();
												}
											})
										] : []
								]),
								m(WatermarkPreviewWidget)
							]
							: STATE.loadingContent
								? [m(Empty, {icon: "sync.fa-spin", size: 2, class: "pad-2"})]
								: []
					)
				])
			)
		],
		({attrs}) => {

			const orga = attrs.orga || MAINSTATE.organization;
			if (orga) enableWatermark(orga);

			else {
				delete STATE.organization;
				delete STATE.streamname;
			}

			document.title = STR.TITLE

		}
	);

export default WatermarkScreen;
